import { useState } from 'react'
import { Head, Link } from '@inertiajs/react'
import AdminLayout from '@/components/AdminLayout'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  ArrowLeft,
  ExternalLink,
  Copy,
  Check,
  Mail,
  Clock,
  Tag,
  AlertCircle,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronRight
} from 'lucide-react'

interface FetchLog {
  id: number
  gmailMessageId: string
  fromEmail: string | null
  subject: string | null
  receivedAt: string | null
  fetchedAt: string
  outcome: 'processed' | 'skipped'
  skipReason: string | null
  skipReasonDisplay: string | null
  emailId: number | null
  gmailUrl: string
  accountId: number
  accountName: string
  createdAt: string
  updatedAt: string
}

interface Email {
  id: number
  gmailMessageId: string
  fromEmail: string | null
  fromName: string | null
  toEmails: string[]
  subject: string | null
  snippet: string | null
  receivedAt: string | null
  processingStatus: string
  startedProcessingAt: string | null
  completedProcessingAt: string | null
  processingDuration: number | null
  errorMessage: string | null
  categoriesOnEntry: string[]
  statusOnEntry: string[]
  labelsOnEntry: string[]
  statusOnExit: string[]
  labelsOnExit: string[]
  gmailUrl: string
  workflowsPassedCount: number
  workflowsFailedCount: number
  totalActionsCount: number
  activityStatus: string
}

interface ProcessingLog {
  id: number
  workflowId: number | null
  workflowName: string
  status: string
  startedAt: string | null
  completedAt: string | null
  durationMs: number | null
  error: string | null
  filters: Array<{
    name: string
    passed: boolean
    conditions?: Array<{
      field: string
      operator: string
      value: string
      result: boolean
    }>
  }>
  actions: Array<{
    type: string
    success: boolean
    error?: string
  }>
}

interface EmailShowProps {
  currentUser?: {
    name: string
    email: string
    avatarUrl?: string
  }
  fetchLog: FetchLog
  email: Email | null
  processingLogs: ProcessingLog[]
  copyableData: string
}

export default function EmailShow({
  currentUser,
  fetchLog,
  email,
  processingLogs,
  copyableData
}: EmailShowProps) {
  const [copied, setCopied] = useState(false)
  const [expandedWorkflows, setExpandedWorkflows] = useState<Set<number>>(new Set())

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(copyableData)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const toggleWorkflow = (id: number) => {
    const newExpanded = new Set(expandedWorkflows)
    if (newExpanded.has(id)) {
      newExpanded.delete(id)
    } else {
      newExpanded.add(id)
    }
    setExpandedWorkflows(newExpanded)
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleString()
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
        return <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">Success</Badge>
      case 'failed':
      case 'error':
        return <Badge className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">Failed</Badge>
      case 'in_progress':
        return <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">In Progress</Badge>
      default:
        return <Badge className="bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400">{status}</Badge>
    }
  }

  const getProcessingStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">Completed</Badge>
      case 'processing':
        return <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">Processing</Badge>
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">Pending</Badge>
      case 'failed':
        return <Badge className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">Failed</Badge>
      case 'completed_with_errors':
        return <Badge className="bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400">Completed with Errors</Badge>
      default:
        return <Badge className="bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400">{status}</Badge>
    }
  }

  return (
    <>
      <Head title={`Email: ${fetchLog.subject || fetchLog.gmailMessageId} - Admin`} />
      <AdminLayout currentUser={currentUser}>
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" asChild>
                <Link href="/admin/emails">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to List
                </Link>
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-blue-950 dark:text-white">
                  Email Details
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Gmail ID: {fetchLog.gmailMessageId}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleCopyToClipboard}
                className="dark:text-gray-300 dark:hover:text-gray-100"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Debug Info
                  </>
                )}
              </Button>
              <Button asChild>
                <a href={fetchLog.gmailUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  View in Gmail
                </a>
              </Button>
            </div>
          </div>

          {/* Fetch Log Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-gray-100">
                <Mail className="w-5 h-5" />
                Fetch Information
              </CardTitle>
              <CardDescription>
                Details about when and how this email was fetched from Gmail
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">From</label>
                  <p className="text-gray-900 dark:text-gray-100">{fetchLog.fromEmail || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Subject</label>
                  <p className="text-gray-900 dark:text-gray-100">{fetchLog.subject || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Received At</label>
                  <p className="text-gray-900 dark:text-gray-100">{formatDate(fetchLog.receivedAt)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Fetched At</label>
                  <p className="text-gray-900 dark:text-gray-100">{formatDate(fetchLog.fetchedAt)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Outcome</label>
                  <p className="mt-1">
                    {fetchLog.outcome === 'processed' ? (
                      <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                        Processed
                      </Badge>
                    ) : (
                      <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                        Skipped
                      </Badge>
                    )}
                  </p>
                </div>
                {fetchLog.skipReason && (
                  <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Skip Reason</label>
                    <p className="text-gray-900 dark:text-gray-100">{fetchLog.skipReasonDisplay}</p>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Account</label>
                  <p className="text-gray-900 dark:text-gray-100">{fetchLog.accountName}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Email Record (if processed) */}
          {email ? (
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-gray-100">
                    <Clock className="w-5 h-5" />
                    Processing Status
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Status</label>
                      <p className="mt-1">{getProcessingStatusBadge(email.processingStatus)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Started</label>
                      <p className="text-gray-900 dark:text-gray-100">{formatDate(email.startedProcessingAt)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Completed</label>
                      <p className="text-gray-900 dark:text-gray-100">{formatDate(email.completedProcessingAt)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Duration</label>
                      <p className="text-gray-900 dark:text-gray-100">
                        {email.processingDuration ? `${email.processingDuration}s` : '-'}
                      </p>
                    </div>
                  </div>
                  {email.errorMessage && (
                    <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-md">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
                        <div>
                          <p className="font-medium text-red-800 dark:text-red-200">Error</p>
                          <p className="text-red-700 dark:text-red-300 text-sm">{email.errorMessage}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-gray-100">
                    <Tag className="w-5 h-5" />
                    Labels & Categories
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-6 sm:grid-cols-2">
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Labels On Entry</label>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {email.labelsOnEntry.length > 0 ? (
                          email.labelsOnEntry.map((label, i) => (
                            <Badge key={i} variant="outline">{label}</Badge>
                          ))
                        ) : (
                          <span className="text-gray-500 dark:text-gray-400">No labels</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Labels On Exit</label>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {email.labelsOnExit.length > 0 ? (
                          email.labelsOnExit.map((label, i) => (
                            <Badge key={i} variant="outline">{label}</Badge>
                          ))
                        ) : (
                          <span className="text-gray-500 dark:text-gray-400">No labels</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Categories</label>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {email.categoriesOnEntry.length > 0 ? (
                          email.categoriesOnEntry.map((cat, i) => (
                            <Badge key={i} variant="secondary">{cat}</Badge>
                          ))
                        ) : (
                          <span className="text-gray-500 dark:text-gray-400">No categories</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Status On Entry</label>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {email.statusOnEntry.length > 0 ? (
                          email.statusOnEntry.map((status, i) => (
                            <Badge key={i} variant="secondary">{status}</Badge>
                          ))
                        ) : (
                          <span className="text-gray-500 dark:text-gray-400">No status</span>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Workflow Processing Logs */}
              {processingLogs.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-gray-900 dark:text-gray-100">Workflow Processing</CardTitle>
                    <CardDescription>
                      {email.workflowsPassedCount} passed, {email.workflowsFailedCount} failed,{' '}
                      {email.totalActionsCount} actions executed
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {processingLogs.map((log) => (
                        <div
                          key={log.id}
                          className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
                        >
                          <button
                            onClick={() => toggleWorkflow(log.id)}
                            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                          >
                            <div className="flex items-center gap-3">
                              {expandedWorkflows.has(log.id) ? (
                                <ChevronDown className="w-5 h-5 text-gray-400" />
                              ) : (
                                <ChevronRight className="w-5 h-5 text-gray-400" />
                              )}
                              <span className="font-medium text-gray-900 dark:text-gray-100">
                                {log.workflowName}
                              </span>
                              {getStatusBadge(log.status)}
                            </div>
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              {log.durationMs ? `${log.durationMs}ms` : ''}
                            </span>
                          </button>

                          {expandedWorkflows.has(log.id) && (
                            <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-800/30">
                              {log.error && (
                                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-md">
                                  <p className="text-red-700 dark:text-red-300 text-sm">{log.error}</p>
                                </div>
                              )}

                              {/* Filters */}
                              {log.filters.length > 0 && (
                                <div className="mb-4">
                                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Filters</h4>
                                  <div className="space-y-2">
                                    {log.filters.map((filter, i) => (
                                      <div key={i} className="flex items-start gap-2">
                                        {filter.passed ? (
                                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                                        ) : (
                                          <XCircle className="w-4 h-4 text-red-500 mt-0.5" />
                                        )}
                                        <div>
                                          <span className="text-sm text-gray-900 dark:text-gray-100">{filter.name}</span>
                                          {filter.conditions && filter.conditions.length > 0 && (
                                            <ul className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                              {filter.conditions.map((cond, j) => (
                                                <li key={j}>
                                                  {cond.field} {cond.operator} "{cond.value}" -{' '}
                                                  {cond.result ? 'matched' : 'not matched'}
                                                </li>
                                              ))}
                                            </ul>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Actions */}
                              {log.actions.length > 0 && (
                                <div>
                                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Actions</h4>
                                  <div className="space-y-2">
                                    {log.actions.map((action, i) => (
                                      <div key={i} className="flex items-start gap-2">
                                        {action.success ? (
                                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                                        ) : (
                                          <XCircle className="w-4 h-4 text-red-500 mt-0.5" />
                                        )}
                                        <div>
                                          <span className="text-sm text-gray-900 dark:text-gray-100">{action.type}</span>
                                          {action.error && (
                                            <p className="text-xs text-red-500 dark:text-red-400">{action.error}</p>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {log.filters.length === 0 && log.actions.length === 0 && (
                                <p className="text-sm text-gray-500 dark:text-gray-400">No filter or action details available</p>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card>
              <CardContent className="py-8">
                <div className="text-center">
                  <AlertCircle className="w-12 h-12 mx-auto text-yellow-500 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                    Email Was Skipped
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    This email was not processed. Reason: {fetchLog.skipReasonDisplay || 'Unknown'}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </AdminLayout>
    </>
  )
}
